import type { CSSProperties, ReactNode } from "react";
import { isActivationKey, isInsideOwnClickTarget } from "./activationKeys";
import { DataTableCell } from "./DataTableCell";
import type { Column } from "./DataTableTypes";
import { type RowLook, rowClassName } from "./rowState";

interface DataTableRowProps<R> {
  row: R;
  columns: Column<R>[];
  query: string;
  /** Position on the page; drives the staggered entrance animation. */
  index: number;
  look: RowLook;
  /** What a click or Enter/Space on the row does; rows without it are not focusable. */
  onActivate?: () => void;
}

export function DataTableRow<R>({ row, columns, query, index, look, onActivate }: DataTableRowProps<R>): React.JSX.Element {
  return (
    <tr
      style={{ ["--i" as string]: index } as CSSProperties}
      className={rowClassName(look)}
      tabIndex={onActivate ? 0 : undefined}
      aria-expanded={look.expandable ? look.open : undefined}
      onClick={(e) => {
        if (isInsideOwnClickTarget(e.target)) return;
        onActivate?.();
      }}
      onKeyDown={
        onActivate
          ? (e) => {
              if (e.target !== e.currentTarget || !isActivationKey(e.key)) return;
              e.preventDefault();
              onActivate();
            }
          : undefined
      }
    >
      {columns.map((col) => (
        <DataTableCell key={col.key} row={row} col={col} query={query} />
      ))}
    </tr>
  );
}

/** The opened content of an expandable row, spanning every column. */
export function ExpandedRow({ colSpan, children }: { colSpan: number; children: ReactNode }): React.JSX.Element {
  return (
    <tr className="nb-dt-expanded">
      <td colSpan={colSpan}>
        <div className="nb-dt-detail">{children}</div>
      </td>
    </tr>
  );
}
